import { useRef, useState } from "react";
import type { PayoutView } from "@/services/types";
import { Modal } from "@/components/base/Modal";
import { Button } from "@/components/base/Button";
import { Textarea } from "@/components/base/Textarea";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";

interface ReversalModalProps {
  payout: PayoutView;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<boolean>;
}

export function ReversalModal({ payout, onClose, onConfirm }: ReversalModalProps) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  const handleConfirm = async () => {
    if (submittingRef.current) return;
    if (!reason.trim()) {
      setError("A reversal reason is required.");
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    setError(null);
    try {
      const ok = await onConfirm(reason.trim());
      if (ok) onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not reverse this payout.");
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Reverse this disbursement?"
      description={`${payout.voucherNumber} — ${payout.winnerName}, ${payout.groupName} cycle ${payout.cycle}`}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="danger" icon={<i className="ri-close-circle-line" />} loading={submitting} onClick={handleConfirm}>
            Reverse payout
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="flex items-center justify-between rounded-md bg-background-100/60 px-3 py-2 text-sm">
          <span className="text-foreground-500">Amount disbursed</span>
          <span className="tnum font-semibold text-foreground-900"><CurrencyDisplay amount={payout.amountPaid} hideSymbol /></span>
        </div>
        <div className="rounded-md bg-amber-50 px-3 py-2.5 text-xs text-amber-800 border border-amber-200">
          Reversing will cancel the customer payout transaction, reset the disbursed amount to 0, and return this voucher to <strong>Pending Approval</strong> so it can be reviewed, updated, and re-disbursed.
        </div>
        <Textarea
          label="Reversal reason (required)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Explain why this disbursement is being reversed…"
          rows={4}
          maxLength={500}
        />
        {error ? <p className="text-xs text-danger-600">{error}</p> : null}
      </div>
    </Modal>
  );
}